import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Card,
  CardContent,
  CardActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClassIcon from '@mui/icons-material/Class';
import { Header } from "../components/Header";
import { getCourses } from "../services/getCourses";
import { getReceipts } from "../services/getReceipts";
import useUserStore from "../store/UserStore";
import { useTranslation } from 'react-i18next';
import { UsuariListButton } from '../components/UsuariListButton.jsx';
import { getReciept } from "../services/getReceipt.js";
import { STPButton } from "../components/Botones.jsx";
import { saveUserAction } from "../services/saveUserAction.js";

export function Recibos({ setIsAuthenticated }) {
  const { t } = useTranslation();
  const user = useUserStore((state) => state.user);
  const [receipts, setReceipts] = useState([]);
  const [showCourseSelection, setShowCourseSelection] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [hasReceipts, setHasReceipts] = useState(null);
  const [lastCourse, setLastCourse] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getCourses()
      .then((response) => {
        const courses = response.data;
        const lastCourse = courses.sort((a, b) => b.curso.codeje - a.curso.codeje)[courses.length - 1];
        setLastCourse(lastCourse);
        const coursesForYear = courses.filter(course => course.curso.codeje === lastCourse.curso.codeje);
        setFilteredCourses(coursesForYear);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching courses:', error);
        setLoading(false);
      });
  }, [user.idpersona]);

  const handleCourseSelect = (courseId) => {
    setSelectedCourse(courseId);
    getReceipts(courseId)
      .then((response) => {
        if (response.data.length === 0) {
          setHasReceipts(false);
        }
        else {
          setReceipts(response.data);
          setHasReceipts(true);
          setShowCourseSelection(false);
        }
      })
      .catch((error) => {
        console.error('Error fetching receipts:', error);
      });
  };

  const handleGoBackToCourses = () => {
    setShowCourseSelection(true);
    setReceipts([]);
  };

  const handleDownload = async (idrecibo) => {
    try {

      setLoading(true);

      const response = await getReciept(idrecibo);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Receipt_${idrecibo}.pdf`);
      document.body.appendChild(link);

      link.click();

      saveUserAction({ id_accion: 6, detalles_accion: `Descarga de recibo ${idrecibo} exitosa` });

      link.remove();

      window.URL.revokeObjectURL(url);

      setLoading(false);

    } catch (error) {
      console.error("Error fetching receipt:", error);
      setLoading(false);
      saveUserAction({ id_accion: 6, detalles_accion: `Error al descargar recibo ${idrecibo}` });
    }
  };

  const filteredReceipts = showPendingOnly
    ? receipts.filter(receipt => receipt.estado === 1) 
    : receipts;

  return (
    loading ? 
    (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress sx={{ 
        color: 'rgba(80, 165, 121, 0.7)',
       }}/>
    </Box>) :

    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100%',
    }}>
      <Header setIsAuthenticated={setIsAuthenticated} />

      {showCourseSelection ? (
        <Box sx={{ flexGrow: 1, padding: 3, width: "100%", margin: "auto" }}>
          <Typography variant="h4" sx={{ mb: 3, color: "black", textAlign: "center" }}>
            {t("userSelection.selectUser")} <span style={{ color: "#50A579" }}>curso</span>
          </Typography>

          <List sx={{ width: "100%", maxWidth: 540 }}>
            {filteredCourses.map((course) => (
              <ListItem key={course.idcurso} disablePadding>
                <UsuariListButton onClick={() => handleCourseSelect(course.idcurso)}>
                  <ListItemIcon>
                    <ClassIcon sx={{ color: "#50A579" }} />
                  </ListItemIcon>
                  <ListItemText primary={course.curso ? course.curso.descripcion : course.idcurso} />
                </UsuariListButton>
              </ListItem>
            ))}
          </List>
          <Snackbar
            open={hasReceipts === false}
            autoHideDuration={6000}
            onClose={() => setHasReceipts(null)}
          >
            <Alert onClose={() => setHasReceipts(null)} severity="error" sx={{ width: '100%' }}>
              {t("receipts.noReceipts")}
            </Alert>
          </Snackbar>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 4, color: "black", textAlign: "center" }}>
            <strong>{t("receipts.title")}</strong>
          </Typography>

          <IconButton
            aria-label="back"
            onClick={handleGoBackToCourses}
            sx={{ mb: 3 }}
          >
            <ArrowBackIcon /> <Typography sx={{ ml: 2 }} variant="body1">Volver</Typography>
          </IconButton>

          <STPButton
            key={showPendingOnly ? "pending" : "all"} // Key para forzar la actualización del botón 
            variant={showPendingOnly ? "contained" : "outlined"}
            onClick={() => setShowPendingOnly(!showPendingOnly)}
            sx={{
              width: "80%",
              margin: "auto",
              backgroundColor: showPendingOnly ? "#f44336" : "#50A579",
              position: "relative",
              top: "10px",
              left: "10%",
              right: "10%",
              
            }}
          >
            <Typography variant="button" sx={{ color: "white", padding: '10px' }}>
              {showPendingOnly ? t("receipts.noPending") : t("receipts.Pending")}
            </Typography>
          </STPButton>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {filteredReceipts.map((receipt) => (
              <Grid item xs={12} md={6} key={receipt.idrecibo}>
                <Card
                  sx={{
                    border: (receipt.estado === 1 && receipt.fvencimiento < new Date(new Date().getMonth() + 1)) ? '2px solid #50A579' :
                      receipt.estado === 1 ? '2px solid orange' :
                        receipt.estado === 2 ? '2px solid #50A579' :
                          receipt.estado === 3 ? '2px solid red' :
                            receipt.estado === 4 ? '2px solid #ccc' :
                              receipt.estado === 5 ? '2px solid #50A579' :
                                receipt.estado === 6 ? '2px solid #50A579' :
                                  '1px solid #ccc',
                    boxShadow: (receipt.estado === 1 && receipt.fvencimiento < new Date(new Date().getMonth() + 1)) ? '0 0 10px rgba(0, 128, 0, 0.5)' :
                      receipt.estado === 1 ? '0 0 10px rgba(255, 165, 0, 0.5)' :
                        receipt.estado === 2 ? '0 0 10px rgba(0, 128, 0, 0.5)' :
                          receipt.estado === 3 ? '0 0 10px rgba(255, 0, 0, 0.5)' :
                            receipt.estado === 4 ? '0 0 10px rgba(0, 0, 0, 0.1)' :
                              receipt.estado === 5 ? '0 0 10px rgba(0, 128, 0, 0.5)' :
                                receipt.estado === 6 ? '0 0 10px rgba(0, 128, 0, 0.5)' :
                                  '0 0 10px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" component="div">
                      {t("receipts.concept")}: {receipt.concepto}
                    </Typography>
                    <Typography color="text.secondary">
                      {t("receipts.date")}: {receipt.fcobro}
                    </Typography>
                    <Typography variant="body2">
                      {t("receipts.receiptNumber")}: {receipt.idrecibo}
                    </Typography>
                    <Typography variant="body2">
                      {t("receipts.status")}: {receipt.valor}
                    </Typography>
                    <Typography variant="body2">
                      {t("receipts.amount")}: {receipt.importe} €
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <STPButton size="small"
                      sx={{
                        backgroundColor: (receipt.estado === 1 && receipt.fvencimiento < new Date(new Date().getMonth() + 1)) ? '#50A579' :
                          receipt.estado === 1 ? 'orange' :
                            receipt.estado === 2 ? '#50A579' :
                              receipt.estado === 3 ? 'red' :
                                receipt.estado === 4 ? '#ccc' :
                                  receipt.estado === 5 ? '#50A579' :
                                    receipt.estado === 6 ? '#50A579' :
                                      '#ccc',
                        '&:hover': {
                          backgroundColor: (receipt.estado === 1 && receipt.fvencimiento < new Date(new Date().getMonth() + 1)) ? '#50A579' :
                            receipt.estado === 1 ? 'orange' :
                              receipt.estado === 2 ? '#50A579' :
                                receipt.estado === 3 ? 'red' :
                                  receipt.estado === 4 ? '#ccc' :
                                    receipt.estado === 5 ? '#50A579' :
                                      receipt.estado === 6 ? '#50A579' :
                                        '#ccc', // Si no se pone el token de color, al clicar el botón se vuelve blanco
                        },
                      }}
                      onClick={() => handleDownload(receipt.idrecibo, user.idpersona)}>{t("receipts.download")}</STPButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
